// Import React state
import { useState } from "react";

// Import your existing popup layout
import TutorialLayout from "../../global/components/tutorials/components/TutorialLayout";

// Import the tutorial styles
import "./css/CreateTemplateTutorial.css";
import CreateButton from "global/components/CreateButton";


// Define the tutorial steps outside the component to avoid rebuilding them on every render
const _tutorialSteps = [
    {
        // Set the heading for the first step
        title: "Name your template",
        // Identify the relevant area of the portal
        section: "Details",
        // Introduce the task in plain language
        description: "Give your template a name and a unique code, then save it.",
        // Provide the actions the user should take
        actions: [
            "Click Add Template to create a new template.",
            "Enter a descriptive title, such as Landscape Prints or Clothing Collection. You'll see this title when choosing templates elsewhere in StrichSuite.",
            "Enter a unique code, such as landscape_prints.",
            "Click Save."
        ],
        // Add a short optional reminder
        tip: "Choose a name you'll recognise when applying the template to other designs."
    },
    {
        // Set the heading for the second step
        title: "Choose a preview image",
        // Identify the relevant area of the portal
        section: "Print Areas",
        // Explain why the preview matters
        description: "Use a preview image to check how artwork looks on your products.",
        // Provide the actions the user should take
        actions: [
            "Start with the default Strich logo, choose a preset shape, upload an image, or select one from your image library.",
            "Try different images to check how landscape, portrait, square, light, dark or detailed artwork fits."
        ],
        // Explain that previews can be changed later
        tip: "You can change the preview image at any time to test different designs.",
        // Give advanced options their own optional explanation
        moreTitle: "More print area options",
        // Describe the optional advanced settings
        moreDescription: "Turn on Advanced to add multiple print areas. Front is selected by default. You can also choose a background colour for each print area."
    },
    {
        // Set the heading for the third step
        title: "Add your products",
        // Identify the relevant area of the portal
        section: "Products",
        // Introduce product selection
        description: "Choose the products you want to include in this template.",
        // Provide the actions the user should take
        actions: [
            "Click Add Products.",
            "Select as many products as you want to include.",
            "Click Done to add them to your template."
        ],
        // Offer a useful example without making it a requirement
        tip: "You could create separate templates for apparel, prints, accessories or different types of artwork."
    },
    {
        // Set the heading for the fourth step
        title: "Customise your products",
        // Identify the relevant area of the portal
        section: "Products",
        // Explain that product settings can be reused
        description: "Open any product you've added to adjust its settings.",
        // Provide the actions the user should take
        actions: [
            "Click a product in your template to open its settings.",
            "Adjust its images, title, description and price if needed.",
            "Check the print areas and change the artwork size or position if needed.",
            "Review how the preview image looks on the product."
        ],
        // Clarify the impact of template settings
        tip: "The settings you choose here are used by designs that use this template.",
        // Keep less common settings out of the main instructions
        moreTitle: "More product print settings",
        // Explain the advanced settings in simple terms
        moreDescription: "You can add a print area background colour and control which artwork proportions and rotations are accepted. These options are useful when a product needs a particular artwork layout."
    }
];





// Define the tutorial steps outside the component to avoid rebuilding them on every render
const tutorialSteps = [
    {
        // Set the heading for the first step
        title: "Name your template",
        // Identify the relevant area of the portal
        section: "Details",
        // Introduce the task in plain language
        description: "Give your template a name and a unique code, then save it.",
        // Provide the actions the user should take
        actions: [
            "Click Add Template to create a new template.",
            "Enter a descriptive title, such as Landscape Prints or Clothing Collection. You'll see this title when choosing templates elsewhere in StrichSuite.",
            "Enter a unique code, such as landscape_prints.",
            "Click Save."
        ],
        // Add a short optional reminder
        tip: "Choose a name you'll recognise when applying the template to other designs.",
        // Image 
        image: "step01"
    },

    {
        // Set the heading for the first step
        title: "Edit the Print Areas ",
        // Identify the relevant area of the portal
        section: "Print Areas",
        // Introduce the task in plain language
        description: "Choose the print areas assigned to this template.",
        // Provide the actions the user should take
        actions: [
            "Basic print areas will apply a design to the front only.",
            "Apply and edit the background colour of the print area.",
            "Select whether the artwork should fit (no cropping) into the print area or fill it (may crop the artwork)", 
        ],
        // Add a short optional reminder
        tip: "You can update the print area details at anytime",
        // Image 
        image: "step02"
    },
    {
        // Set the heading for the second step
        title: "Choose multiple Print Areas (optional)",
        // Identify the relevant area of the portal
        section: "Print Areas",
        // Explain why the preview matters
        description: "If multiple print areas are selected then designs with multiple print areas will display with this template.",
        // Provide the actions the user should take
        actions: [
            "Click 'Advanced' to select multiple print areas.",
            "Edit each print area individually."
        ],
        // Explain that previews can be changed later
        tip: "You can add or remove print areas at any time.",
         // Image 
        image: "step03"
    },
    {
        // Set the heading for the second step
        title: "Choose preview image",
        // Identify the relevant area of the portal
        section: "Print Areas",
        // Explain why the preview matters
        description: "If multiple print areas are selected then designs with multiple print areas will display with this template.",
        // Provide the actions the user should take
        actions: [
            "Start with the default Strich logo, choose a preset shape, upload an image, or select one from your image library.",
            "Try different images to check how landscape, portrait, square, light, dark or detailed artwork fits."
        ],
        // Explain that previews can be changed later
        tip: "You can change the preview image at any time to test different designs.",
        // Give advanced options their own optional explanation
        //moreTitle: "More print area options",
        // Describe the optional advanced settings
        //moreDescription: "Turn on Advanced to add multiple print areas. Front is selected by default. You can also choose a background colour for each print area.",
        // Image 
        image: "step04"
    },



    {
        // Set the heading for the third step
        title: "Add your products",
        // Identify the relevant area of the portal
        section: "Products",
        // Introduce product selection
        description: "Choose the products you want to include in this template.",
        // Provide the actions the user should take
        actions: [
            "Click Add Products.",
        ],
        // Offer a useful example without making it a requirement
        tip: "You could create separate templates for apparel, prints, accessories or different types of artwork.",
        // Image 
        image: "step05"
    },
    {
        // Set the heading for the third step
        title: "Choose your products",
        // Identify the relevant area of the portal
        section: "Products",
        // Introduce product selection
        description: "Choose the products you want to include in this template.",
        // Provide the actions the user should take
        actions: [
            "Click on the product.",
            "Choose as many products as you like.",
            "Use the filters to find the products you want.",
            "Click Done to add them to your template."
        ],
        // Offer a useful example without making it a requirement
        tip: "You could create separate templates for apparel, prints, accessories or different types of artwork.",
        // Image 
        image: "step06"
    },
    {
        // Set the heading for the third step
        title: "View and edit your choosen products",
        // Identify the relevant area of the portal
        section: "Products",
        // Introduce product selection
        description: "View all the products assigned to this template.",
        // Provide the actions the user should take
        actions: [
            "Add or remove products.",
            "Set a product inactive if you do not want it to display on the stores.",
            "Click on a product to edit it.",
        ],
        // Offer a useful example without making it a requirement
        tip: "You can add multiples of the same product if for example you want one t-shirt with artwork printed centre chest and another where the artwork is printed left chest.",
        // Image 
        image: "step07"
    },


    {
        // Set the heading for the third step
        title: "Edit a product",
        // Identify the relevant area of the portal
        section: "Product",
        // Introduce product selection
        description: "If the product is parent child then you will see the parent and child list on the left",
        // Provide the actions the user should take
        actions: [
            "Select the parent or a child in the left hand list to edit it.",
            "Click 'View All Children' to view all the children in a table, which makes it easier to edit them all in one place.",
        ],
        // Offer a useful example without making it a requirement
        tip: "Making changes to a parent will cascade down into the children, however making changes to a child will affect that child only.",
        // Image 
        image: "step08"
    },
    {
        // Set the heading for the third step
        title: "Edit product images",
        // Identify the relevant area of the portal
        section: "Product",
        // Introduce product selection
        description: "View the images for this product. These are made up of views (quick renders) and mockups (generated images).",
        // Provide the actions the user should take
        actions: [
            "Toggle 'Auto Mockups'.",
            "If you've made changes and the images haven't updated then you can click 'Refresh' to recreate them.",
            "Click 'Manage Images' to amend them."
        ],
        // Offer a useful example without making it a requirement
        tip: "Main mockups are randomly selected for each rotation configuration and appear first in the product gallery. Additional mockups are selected separately and appended after all Main images. Set Additional to 0 to exclude them, or turn Auto Mockups off to select images manually.",
        // Image 
        image: "step09"
    },
    {
        // Set the heading for the third step
        title: "Manage images",
        // Identify the relevant area of the portal
        section: "Product",
        // Introduce product selection
        description: "Amend the images assigned to this product, you can choose from the mockup images available or add your own.",
        // Provide the actions the user should take
        actions: [
            "Click to add remove selected images.",
            "Drag and drop to reorder the images in the list."
        ],
        // Offer a useful example without making it a requirement
        tip: "Mockup images are automatically generated featuring the design artwork provided, and in the colourway.",
        // Image 
        image: "step10"
    },
    {
        // Set the heading for the third step
        title: "Product details",
        // Identify the relevant area of the portal
        section: "Product",
        // Introduce product selection
        description: "View the product details, these are from the canonical product data but can be amended if you want.",
        // Provide the actions the user should take
        actions: [
            "Edit any of the details.",
        ],
        // Offer a useful example without making it a requirement
        tip: "Parent product details will show when the customer hasn't yet selected a child product.",
        // Image 
        image: "step11"
    },








    {
        // Set the heading for the fourth step
        title: "Customise your products",
        // Identify the relevant area of the portal
        section: "Products",
        // Explain that product settings can be reused
        description: "Open any product you've added to adjust its settings.",
        // Provide the actions the user should take
        actions: [
            "Click a product in your template to open its settings.",
            "Adjust its images, title, description and price if needed.",
            "Check the print areas and change the artwork size or position if needed.",
            "Review how the preview image looks on the product."
        ],
        // Clarify the impact of template settings
        tip: "The settings you choose here are used by designs that use this template.",
        // Keep less common settings out of the main instructions
        moreTitle: "More product print settings",
        // Explain the advanced settings in simple terms
        moreDescription: "You can add a print area background colour and control which artwork proportions and rotations are accepted. These options are useful when a product needs a particular artwork layout.",
        // Image 
        image: "step04"
    }




];







// Render the main three-step Inline Shopping tutorial
export default function InlineShoppingTutorial({ storeId, imageTag = "", floatingOnly = false, screenshots = {} }) {


    // Track the current tutorial step
    const [step, setStep] = useState(0);

    // Track whether the optional explanation is expanded
    const [isMoreOpen, setIsMoreOpen] = useState(false);

    // Get the current step from the tutorial data
    const currentStep = tutorialSteps[step];

    // Track whether the user has reached the completion screen
    const isComplete = step === tutorialSteps.length;



    // Move to a specific step without leaving the popup
    function goToStep(nextStep) {

        // Keep the selected step within the supported range
        setStep(Math.min(tutorialSteps.length, Math.max(0, nextStep)));

    }
  
    const totalNumberOfSteps = tutorialSteps.length;

    //console.log("totalNumberOfSteps: ", totalNumberOfSteps);

    const footerNextBack = <nav className="inline-shopping__navigation" aria-label="Tutorial navigation">
                            
                                <CreateButton type="button" title="← Back" size="m" plain={true} onClick={() => goToStep(step - 1)} disabled={step === 0} />

                                <div className="inline-shopping__dots" aria-label={`Screen ${step} of ${totalNumberOfSteps}`}>
                                    {tutorialSteps.map((tutorialStep, index) => (
                                        <button type="button" key={index} className={index === step ? "inline-shopping__dot inline-shopping__dot--active" : "inline-shopping__dot"} aria-label={`Go to screen ${index}`} aria-current={index === step ? "step" : undefined} onClick={() => goToStep(index)} />
                                    ))}
                                </div>

                                <CreateButton title={step === (tutorialSteps.length - 1) ? "Start again" : step === tutorialSteps.length ? "Finish →" : "Next step →"} size="m" type="button" onClick={() => goToStep(step === tutorialSteps.length ? 0 : step + 1)} />
                            
                            </nav>



    // Render the tutorial inside the existing portal popup
    return (

        <TutorialLayout title="Create a Template" description="Simple steps to create a preset template of products to apply to your designs" minutes={5} steps={3} footer={footerNextBack} setStep={setStep}>

            {/* Display one compact tutorial step at a time */}
            <section className="create-template-tutorial" aria-label="Create a Template guide">

                {/* Show the current step and overall progress */}
                <div className="create-template-tutorial__progress">

                    {/* Explain progress in text for accessibility */}
                    <span className="create-template-tutorial__progress-label">

                        {isComplete ? "Guide complete" : `Step ${step + 1} of ${tutorialSteps.length}`}

                    </span>

                    {/* Display a simple progress bar */}
                    <div
                        className="create-template-tutorial__progress-track"
                        role="progressbar"
                        aria-label="Tutorial progress"
                        aria-valuemin={0}
                        aria-valuemax={tutorialSteps.length}
                        aria-valuenow={isComplete ? tutorialSteps.length : step + 1}
                    >

                        {/* Fill the progress bar according to the current step */}
                        <div
                            className="create-template-tutorial__progress-fill"
                            style={{ width: `${((isComplete ? tutorialSteps.length : step + 1) / tutorialSteps.length) * 100}%` }}
                        />

                    </div>

                </div>

                {/* Display a completion message after all four steps */}
                {isComplete ? (

                    <div className="create-template-tutorial__step">

                        {/* Confirm that the guide has finished */}
                        <h2 className="create-template-tutorial__step-title">Your template is ready!</h2>

                        {/* Explain where the template can be used */}
                        <p className="create-template-tutorial__description">
                            You can now use your template with designs in the StrichSuite portal or with Inline Shopping.
                        </p>

                        {/* Reinforce the main benefit */}
                        <p className="create-template-tutorial__tip">
                            Tip: Create more templates whenever you need a different collection of products or print settings.
                        </p>

                    </div>

                ) : (

                    <div >

                        <div className="create-template-tutorial__step" key={step}>

                            <div>

                                {/* Show the relevant portal section */}
                                <span className="create-template-tutorial__section-label">{currentStep.section} section</span>

                                {/* Show the current step heading */}
                                <h2 className="create-template-tutorial__step-title">{currentStep.title}</h2>

                                {/* Introduce the current task */}
                                <p className="create-template-tutorial__description">{currentStep.description}</p>

                                {/* List only the actions needed for this step */}
                                <ol className="create-template-tutorial__actions">

                                    {/* Render each action as a numbered instruction */}
                                    {currentStep.actions.map((action, index) => (

                                        <li className="create-template-tutorial__action" key={`${step}-${index}`}>
                                            {action}
                                        </li>

                                    ))}

                                </ol>

                                {/* Show a short practical tip */}
                                <p className="create-template-tutorial__tip">Tip: {currentStep.tip}</p>

                                {/* Only offer advanced explanations when they exist */}
                                {currentStep.moreTitle && (

                                    <div className="create-template-tutorial__more">

                                        {/* Let the user choose whether to read advanced information */}
                                        <button
                                            className="create-template-tutorial__more-toggle"
                                            type="button"
                                            aria-expanded={isMoreOpen}
                                            onClick={() => setIsMoreOpen((previousValue) => !previousValue)}
                                        >

                                            {/* Show the optional explanation title */}
                                            <span>{currentStep.moreTitle}</span>

                                            {/* Use a simple character rather than an icon dependency */}
                                            <span aria-hidden="true">{isMoreOpen ? "−" : "+"}</span>

                                        </button>

                                        {/* Only display advanced details when requested */}
                                        {isMoreOpen && (

                                            <p className="create-template-tutorial__more-description">
                                                {currentStep.moreDescription}
                                            </p>

                                        )}

                                    </div>

                                )}

                            </div>

                            { currentStep.image &&

                                <div className="create-template-tutorial__image">
                                    <img src={`/images/tutorial/create-template/${currentStep.image}.png`} alt={currentStep.title} style={{maxWidth: "100%"}} />
                                </div>

                            }


                        </div>

                       

                    </div>

                )}

                
               
            </section>

            

        </TutorialLayout>

    )

}























